import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    try {
      const errorData = JSON.parse(text);
      throw new Error(`${res.status}: ${errorData.message || errorData.error || text}`);
    } catch (parseError) {
      throw new Error(`${res.status}: ${text}`);
    }
  }
}

// export async function apiRequest(url:string, method='GET', body?:any){
//   return fetch(url, { method, headers:{'Content-Type':'application/json'}, body: body?JSON.stringify(body):undefined });
// }
// in client/src/lib/queryClient.ts
export async function apiRequest(
  url: string,
  method: "GET"|"POST"|"PUT"|"PATCH"|"DELETE",
  body?: any
) {
  const headers: Record<string,string> = {
    "Content-Type": "application/json"
  };

  // if you’re using JWT you can pull it from your auth hook:
  // const { token } = useAuth();
  // if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(url, {
    method,
    credentials: "include",    // ← this sends `connect.sid` or whatever
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  if (!resp.ok) {
    const text = await resp.text();
    try {
      const errorData = JSON.parse(text);
      throw new Error(errorData.message || errorData.error || text || resp.statusText);
    } catch (parseError) {
      throw new Error(text || resp.statusText);
    }
  }

  return resp;
}

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(opts: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401 }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",  // ← also include cookies here
      headers: {
        "Accept": "application/json",
      },
    });

    if (res.status === 401) {
      
      // Clear all localStorage on 401
      if (typeof window !== 'undefined') {
        localStorage.clear();
      }
      
      if (on401 === "returnNull") {
        return null as any;
      }
    }

    await throwIfResNotOk(res);
    return res.json();
  };

// Hierarchical cache keys for unified chapter system
export const chapterQueryKeys = {
  all: ['chapters'] as const,
  lists: () => [...chapterQueryKeys.all, 'list'] as const,
  list: (projectId: number) => [...chapterQueryKeys.lists(), projectId] as const,
  previews: () => [...chapterQueryKeys.all, 'preview'] as const,
  preview: (projectId: number) => [...chapterQueryKeys.previews(), projectId] as const,
  details: () => [...chapterQueryKeys.all, 'detail'] as const,
  detail: (chapterId: string) => [...chapterQueryKeys.details(), chapterId] as const,
  full: () => [...chapterQueryKeys.all, 'full'] as const,
  fullDetail: (chapterId: string) => [...chapterQueryKeys.full(), chapterId] as const,
  versions: () => [...chapterQueryKeys.all, 'versions'] as const,
  chapterVersions: (chapterId: string) => [...chapterQueryKeys.versions(), chapterId] as const,
  version: (chapterId: string, versionNumber: number) => [...chapterQueryKeys.chapterVersions(chapterId), versionNumber] as const,
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      refetchOnWindowFocus: false, // Disable aggressive refetching
      staleTime: 300_000, // 5 minutes for most queries
      retry: false,
    },
    // FIX: The default mutation function signature in TanStack Query v5 is incompatible
    // with the original code. The context object is not passed to mutationFn.
    // Casting to `any` preserves the logic for a potentially custom setup while fixing the type error.
    // Also fixed the inner type error for `mutationKey` to match `readonly unknown[]`.
    mutations: {
      mutationFn: (async (
        variables: unknown,
        context?: { mutationKey?: readonly unknown[] }
      ) => {
        if (!context?.mutationKey?.[0]) {
          throw new Error("Mutation key is required");
        }
        return apiRequest(context.mutationKey[0] as string, "POST", variables);
      }) as any,
      retry: false,
    },
  },
});